class ColorSwatches extends HTMLElement {
  constructor() {
    super();
    this.currentProductId = this.dataset.currentProductId;
    this.baseTitle = this.dataset.baseTitle;
    this.productTitle = this.dataset.productTitle;
    this.relatedProducts = [];
    this.init();
  }

  async init() {
    if (!this.currentProductId) {
      console.warn('⚠️ ColorSwatches: Missing currentProductId data attribute');
      this.hideComponent('Missing currentProductId');
      return;
    }

    if (!this.baseTitle && this.productTitle) {
      const titleParts = this.productTitle.split(' - ');
      if (titleParts.length > 1) {
        this.baseTitle = titleParts.slice(0, -1).join(' - ');
      }
    }

    if (!this.baseTitle) {
      console.warn('⚠️ ColorSwatches: Missing baseTitle data attribute');
      this.hideComponent('Missing baseTitle');
      return;
    }

    try {
      await this.fetchRelatedProducts();
      this.renderSwatches();
    } catch (error) {
      console.error('❌ Error initializing color swatches:', error);
    }
  }

  async fetchRelatedProducts() {
    const query = `
      query getRelatedProducts($query: String!) {
        products(first: 50, query: $query) {
          edges {
            node {
              id
              title
              handle
              featuredImage {
                id
                url
                altText
              }
              variants(first: 1) {
                edges {
                  node {
                    id
                    availableForSale
                  }
                }
              }
            }
          }
        }
      }
    `;

    try {
      const response = await fetch(`https://${this.getShopDomain()}/api/graphql.json`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': this.getStorefrontToken(),
        },
        body: JSON.stringify({
          query,
          variables: { query: this.baseTitle }
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.errors) {
        console.error('GraphQL errors:', data.errors);
        this.hideComponent('GraphQL returned errors');
        return;
      }

      this.processGraphQLData(data);
      
    } catch (error) {
      console.error('Error fetching related products:', error);
      this.hideComponent('GraphQL request failed');
    }
  }

  processGraphQLData(data) {
    if (!data.data.products || !data.data.products.edges) {
      console.warn('⚠️ ColorSwatches: No products data in response');
      this.hideComponent('No products data in GraphQL response');
      return;
    }
    
    // Process and filter related products efficiently
    this.relatedProducts = data.data.products.edges
      .map(edge => edge.node)
      .filter(product => {
        const productTitleParts = product.title.split(' - ');
        const productBaseTitle = productTitleParts.slice(0, -1).join(' - ');
        const productColor = productTitleParts[productTitleParts.length - 1];
        
        return productBaseTitle === this.baseTitle && 
               product.id !== this.currentProductId && 
               product.title !== this.productTitle &&
               productColor;
      })
      .map(product => {
        const titleParts = product.title.split(' - ');
        const color = titleParts[titleParts.length - 1];
        
        return {
          id: product.id,
          title: product.title,
          handle: product.handle,
          url: `/products/${product.handle}`,
          color: color,
          swatchImageName: color.toLowerCase().replace(/[\s\/]+/g, '-') + '.png',
          available: product.variants.edges[0]?.node.availableForSale || false
        };
      });

    //console.log('🎯 ColorSwatches: Found', this.relatedProducts.length, 'related products');
  }

  getShopDomain() {
    // Get shop domain from current URL or config
    const hostname = window.location.hostname;
    if (hostname.includes('myshopify.com')) {
      return hostname;
    }
    // Fallback to config if available
    return window.SHOPIFY_CONFIG?.shopDomain || hostname;
  }

  getStorefrontToken() {
    // Get storefront token from config
    return window.SHOPIFY_CONFIG?.storefrontToken || '';
  }

  hideComponent(reason) {
    console.log(`🚫 ColorSwatches: Hiding swatches - ${reason}`);
    
    // Hide only the swatches container, keep the color text visible
    const swatchesContainer = this.querySelector('.color-swatches__container');
    const header = this.querySelector('.color-swatches__header');
    
    if (swatchesContainer) {
      swatchesContainer.style.display = 'none';
    }
    
    if (header) {
      header.style.display = 'none';
    }
    
    // Keep the component visible so the color text shows
    this.style.display = 'block';
  }

  renderSwatches() {
    if (this.relatedProducts.length === 0) {
      this.hideComponent('No related products found');
      return;
    }

    const container = this.querySelector('.color-swatches__container');
    if (!container) {
      console.warn('⚠️ ColorSwatches: Container not found');
      this.hideComponent('Container not found');
      return;
    }

    // Clear and rebuild swatches
    container.innerHTML = '';
    container.appendChild(this.createCurrentProductSwatch());
    
    this.relatedProducts.forEach(product => {
      container.appendChild(this.createSwatchElement(product));
    });
  }


  createCurrentProductSwatch() {
    const currentColor = this.productTitle.split(' - ').pop();
    const swatchImageName = currentColor.toLowerCase().replace(/[\s\/]+/g, '-') + '.png';
    
    const swatchDiv = document.createElement('div');
    swatchDiv.className = 'color-swatch color-swatch--current';
    swatchDiv.dataset.productId = this.currentProductId;

    const link = document.createElement('a');
    link.href = window.location.pathname;
    link.className = 'color-swatch__link';

    const imageDiv = document.createElement('div');
    imageDiv.className = 'color-swatch__image';
    
    const img = document.createElement('img');
    img.src = `https://${this.getShopDomain()}/cdn/shop/files/${swatchImageName}`;
    img.alt = currentColor;
    img.loading = 'lazy';
    img.width = 100;
    img.height = 100;
    img.onerror = () => {
      img.style.display = 'none';
      const placeholder = document.createElement('div');
      placeholder.className = 'color-swatch__placeholder';
      placeholder.textContent = currentColor.length > 15 ? 
        currentColor.substring(0, 15) + '...' : currentColor;
      imageDiv.appendChild(placeholder);
    };
    
    imageDiv.appendChild(img);

    const labelDiv = document.createElement('div');
    labelDiv.className = 'color-swatch__label';
    labelDiv.textContent = currentColor;

    link.appendChild(imageDiv);
    link.appendChild(labelDiv);
    swatchDiv.appendChild(link);

    return swatchDiv;
  }

  createSwatchElement(product) {
    const swatchDiv = document.createElement('div');
    swatchDiv.className = 'color-swatch';
    swatchDiv.dataset.productId = product.id;

    const link = document.createElement('a');
    link.href = product.url;
    link.className = 'color-swatch__link';

    const imageDiv = document.createElement('div');
    imageDiv.className = 'color-swatch__image';

    const img = document.createElement('img');
    img.src = `https://${this.getShopDomain()}/cdn/shop/files/${product.swatchImageName}`;
    img.alt = product.color;
    img.loading = 'lazy';
    img.width = 100;
    img.height = 100;
    img.onerror = () => {
      img.style.display = 'none';
      const placeholder = document.createElement('div');
      placeholder.className = 'color-swatch__placeholder';
      placeholder.textContent = product.color.length > 15 ? 
        product.color.substring(0, 15) + '...' : product.color;
      imageDiv.appendChild(placeholder);
    };
    
    imageDiv.appendChild(img);

    const labelDiv = document.createElement('div');
    labelDiv.className = 'color-swatch__label';
    labelDiv.textContent = product.color;

    link.appendChild(imageDiv);
    link.appendChild(labelDiv);
    swatchDiv.appendChild(link);

    return swatchDiv;
  }

}

// Register the custom element
if (!customElements.get('color-swatches')) {
  customElements.define('color-swatches', ColorSwatches);
}

export default ColorSwatches;
